import React, { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import KpiRow from '../shared/KpiRow';
import KpiTile from '../shared/KpiTile';
import ChartPanel from '../shared/ChartPanel';
import DateRangePicker from '../shared/DateRangePicker';
import DistributionBar from '../shared/DistributionBar';
import ComparisonSelector from '../shared/ComparisonSelector';
import { useAnalysisRange } from '../../../hooks/analysis/useAnalysisRange';
import { useSalesAnalysis } from '../../../hooks/analysis/useSalesAnalysis';
import { Granularity } from '../../../utils/analytics/types';
import { RevenueMode } from '../../../utils/analytics/revenueMetrics';
import { ComparisonMode, COMPARISON_NOTE } from '../../../utils/analytics/comparison';
import { formatCurrency } from '../../../utils/formatters';
import { AXIS_PROPS, CHART_INK, SERIES_COLORS } from '../shared/chartTheme';

const SalesAnalysis: React.FC = () => {
  const { range, setRange } = useAnalysisRange();
  const [granularity, setGranularity] = useState<Granularity>('day');
  const [mode, setMode] = useState<RevenueMode>('arrival');
  const [comparison, setComparison] = useState<ComparisonMode>('none');
  const data = useSalesAnalysis(range, granularity, mode, comparison);

  const hasRevenue = data.series.some(p => p.value > 0);
  const compareNote = comparison !== 'none' ? COMPARISON_NOTE[comparison] : undefined;

  return (
    <div>
      <div className="analysis-controls">
        <DateRangePicker value={range} onChange={setRange} />
        <ComparisonSelector value={comparison} onChange={setComparison} />
        <div className="analysis-toggle-group" role="group" aria-label="Revenue attribution">
          <button className={mode === 'arrival' ? 'active' : ''} onClick={() => setMode('arrival')}>
            By arrival
          </button>
          <button className={mode === 'stay-night' ? 'active' : ''} onClick={() => setMode('stay-night')}>
            By stay night
          </button>
        </div>
      </div>

      <KpiRow>
        <KpiTile
          label="Revenue"
          value={formatCurrency(data.totalRevenue)}
          delta={data.deltas?.revenue}
          note={compareNote ?? (mode === 'arrival' ? 'by arrival date' : 'by stay night')}
        />
        <KpiTile label="Bookings" value={String(data.bookings)} delta={data.deltas?.bookings} note={compareNote ?? 'arrivals in range'} />
        <KpiTile
          label="ADR"
          value={data.adr !== null ? formatCurrency(data.adr) : '—'}
          delta={data.deltas?.adr}
          note={compareNote ?? 'avg daily rate / site-night'}
        />
        <KpiTile
          label="RevPAS"
          value={data.revpas !== null ? formatCurrency(data.revpas) : '—'}
          delta={data.deltas?.revpas}
          note={compareNote ?? 'revenue / available site-night'}
        />
        <KpiTile
          label="Median lead time"
          value={data.medianLead !== null ? `${Math.round(data.medianLead)} days` : '—'}
          note="booking → arrival"
        />
      </KpiRow>

      <ChartPanel
        title="Revenue trend"
        subtitle={`Bucketed by ${granularity}`}
        loading={data.loading}
        error={data.error}
        empty={!hasRevenue}
        table={{
          columns: [
            { key: 'label', label: 'Period' },
            { key: 'value', label: 'Revenue', align: 'right', format: formatCurrency },
            { key: 'bookings', label: 'Bookings', align: 'right' },
          ],
          rows: data.series,
        }}
        csv={{
          filename: `revenue-${range.from}-${range.to}`,
          columns: [
            { key: 'bucket', label: 'Period' },
            { key: 'value', label: 'Revenue' },
            { key: 'bookings', label: 'Bookings' },
          ],
          rows: data.series,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <div className="analysis-toggle-group" role="group" aria-label="Granularity">
            {(['day', 'week', 'month'] as Granularity[]).map(g => (
              <button key={g} className={granularity === g ? 'active' : ''} onClick={() => setGranularity(g)}>
                {g.charAt(0).toUpperCase() + g.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data.series} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
            <CartesianGrid stroke={CHART_INK.grid} vertical={false} />
            <XAxis dataKey="label" {...AXIS_PROPS} minTickGap={24} />
            <YAxis {...AXIS_PROPS} tickFormatter={v => `$${Number(v).toLocaleString()}`} width={70} />
            <Tooltip formatter={(value: any) => [formatCurrency(Number(value)), 'Revenue']} />
            <Line
              type="monotone"
              dataKey="value"
              stroke={SERIES_COLORS[0]}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartPanel>

      <ChartPanel
        title="Revenue by booking lead time"
        subtitle="How far ahead guests book, weighted by revenue"
        loading={data.loading}
        error={data.error}
        empty={data.leadBuckets.every(b => b.bookings === 0)}
        table={{
          columns: [
            { key: 'label', label: 'Lead time' },
            { key: 'bookings', label: 'Bookings', align: 'right' },
            { key: 'revenue', label: 'Revenue', align: 'right', format: formatCurrency },
          ],
          rows: data.leadBuckets,
        }}
      >
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data.leadBuckets} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
            <CartesianGrid stroke={CHART_INK.grid} vertical={false} />
            <XAxis dataKey="label" {...AXIS_PROPS} />
            <YAxis {...AXIS_PROPS} tickFormatter={v => `$${Number(v).toLocaleString()}`} width={70} />
            <Tooltip formatter={(value: any) => [formatCurrency(Number(value)), 'Revenue']} />
            <Bar dataKey="revenue" fill={SERIES_COLORS[0]} radius={[4, 4, 0, 0]} maxBarSize={64} />
          </BarChart>
        </ResponsiveContainer>
      </ChartPanel>

      <ChartPanel
        title="Revenue by site type"
        subtitle="Per-site revenue (site cost), grouped by configured site type"
        loading={data.loading}
        error={data.error}
        empty={data.siteTypeRevenue.length === 0}
      >
        <DistributionBar
          segments={data.siteTypeRevenue.map(row => ({
            label: row.typeName,
            value: row.revenue,
            display: formatCurrency(row.revenue),
          }))}
        />
      </ChartPanel>
    </div>
  );
};

export default SalesAnalysis;
